import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { PlusIcon, TrashIcon, CoinsIcon, GlobeIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Library = () => {
  const [activeTab, setActiveTab] = useState('currencies');
  
  // Currencies state
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [showAddCurrencyModal, setShowAddCurrencyModal] = useState(false);
  const [newCurrency, setNewCurrency] = useState({ code: '', symbol: '' });
  const [baseCurrency, setBaseCurrency] = useState('TRY');
  
  // Countries state
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCountryModal, setShowAddCountryModal] = useState(false);
  const [showEditCountryModal, setShowEditCountryModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [countryFormData, setCountryFormData] = useState({ name: '', code: '' });

  // Major world currencies
  const MAJOR_CURRENCIES = [
    { code: 'TRY', symbol: '₺', name: 'Türk Lirası' },
    { code: 'USD', symbol: '$', name: 'ABD Doları' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'İngiliz Sterlini' },
    { code: 'JPY', symbol: '¥', name: 'Japon Yeni' },
    { code: 'CHF', symbol: 'Fr', name: 'İsviçre Frangı' },
    { code: 'CAD', symbol: 'C$', name: 'Kanada Doları' },
    { code: 'AUD', symbol: 'A$', name: 'Avustralya Doları' },
    { code: 'CNY', symbol: '¥', name: 'Çin Yuanı' },
    { code: 'SEK', symbol: 'kr', name: 'İsveç Kronu' }
  ];

  useEffect(() => {
    fetchCurrencies();
    fetchCountries();
  }, []);

  const fetchCurrencies = async () => {
    setCurrenciesLoading(true);
    try {
      const response = await axios.get(`${API}/currencies`);
      const data = response.data?.data || response.data || [];
      setCurrencies(Array.isArray(data) ? data : []);
      
      const settingsRes = await axios.get(`${API}/company-settings`);
      if (settingsRes.data?.base_currency) {
        setBaseCurrency(settingsRes.data.base_currency);
      }
    } catch (error) {
      console.error('Para birimleri yüklenirken hata:', error);
      toast.error('Para birimleri yüklenirken hata oluştu');
    } finally {
      setCurrenciesLoading(false);
    }
  };

  const fetchCountries = async () => {
    setCountriesLoading(true);
    try {
      const response = await axios.get(`${API}/categories/countries`);
      const countriesData = response.data?.countries || response.data?.data || response.data || [];
      setCountries(countriesData);
    } catch (error) {
      console.error('Ülkeler yüklenirken hata:', error);
      toast.error('Ülkeler yüklenirken hata oluştu');
      setCountries([]);
    } finally {
      setCountriesLoading(false);
    }
  };

  const handleAddCountry = async () => {
    if (!countryFormData.name.trim() || !countryFormData.code.trim()) {
      toast.error('Ülke adı ve kodu gerekli');
      return;
    }

    try {
      await axios.post(`${API}/categories/countries`, {
        name: countryFormData.name,
        code: countryFormData.code.toUpperCase()
      });
      
      toast.success('Ülke eklendi');
      setShowAddCountryModal(false);
      setCountryFormData({ name: '', code: '' });
      fetchCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ülke eklenemedi');
    }
  };

  const handleEditCountry = (country) => {
    if (country.is_system) {
      toast.error('Sistem ülkeleri düzenlenemez');
      return;
    }
    setEditingCountry(country);
    setCountryFormData({ name: country.name, code: country.code });
    setShowEditCountryModal(true);
  };

  const handleUpdateCountry = async () => {
    if (!countryFormData.name.trim() || !countryFormData.code.trim()) {
      toast.error('Ülke adı ve kodu gerekli');
      return;
    }

    try {
      await axios.put(`${API}/categories/countries/${editingCountry.id}`, {
        name: countryFormData.name,
        code: countryFormData.code.toUpperCase()
      });
      
      toast.success('Ülke güncellendi');
      setShowEditCountryModal(false);
      setEditingCountry(null);
      setCountryFormData({ name: '', code: '' });
      fetchCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ülke güncellenemedi');
    }
  };

  const handleDeleteCountry = async (countryId, isSystem) => {
    if (isSystem) {
      toast.error('Sistem ülkeleri silinemez');
      return;
    }

    if (!window.confirm('Bu ülkeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`${API}/categories/countries/${countryId}`);
      toast.success('Ülke silindi');
      fetchCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ülke silinemedi');
    }
  };

  const handleAddCurrency = async () => {
    if (!newCurrency.code.trim() || !newCurrency.symbol.trim()) {
      toast.error('Para birimi kodu ve sembolü gerekli');
      return;
    }

    try {
      await axios.post(`${API}/currencies`, {
        code: newCurrency.code.toUpperCase(),
        symbol: newCurrency.symbol
      });
      
      toast.success('Para birimi eklendi');
      setShowAddCurrencyModal(false);
      setNewCurrency({ code: '', symbol: '' });
      fetchCurrencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Para birimi eklenemedi');
    }
  };

  const handleDeleteCurrency = async (code) => {
    if (!window.confirm(`${code} para birimini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/currencies/${code}`);
      toast.success('Para birimi silindi');
      fetchCurrencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Para birimi silinemedi');
    }
  };

  const handleUpdateBaseCurrency = async (code) => {
    try {
      await axios.put(`${API}/company-settings`, { base_currency: code });
      setBaseCurrency(code);
      toast.success('Ana para birimi güncellendi');
    } catch (error) {
      toast.error('Ana para birimi güncellenemedi');
    }
  };

  // Ülke adlarını kısalt
  const getDisplayCountryName = (name) => {
    const shortNames = {
      'Amerika Birleşik Devletleri': 'ABD',
      'Birleşik Arap Emirlikleri': 'BAE',
      'Kongo Demokratik Cumhuriyeti': 'Kongo'
    };
    return shortNames[name] || name;
  };

  const filteredCountries = countries.filter(country =>
    country.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <Card className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="currencies" className="flex items-center gap-2">
              <CoinsIcon className="h-4 w-4" />
              Para Birimleri
            </TabsTrigger>
            <TabsTrigger value="countries" className="flex items-center gap-2">
              <GlobeIcon className="h-4 w-4" />
              Ülkeler
            </TabsTrigger>
          </TabsList>

          {/* Para Birimleri Tab */}
          <TabsContent value="currencies">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Para Birimleri</h3>
                  <p className="text-sm text-gray-500">Şirket para birimlerini yönetin</p>
                </div>
                <Dialog open={showAddCurrencyModal} onOpenChange={setShowAddCurrencyModal}>
                  <Button
                    onClick={() => setShowAddCurrencyModal(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Para Birimi Ekle
                  </Button>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Yeni Para Birimi Ekle</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Para Birimi Kodu *</Label>
                        <Input
                          value={newCurrency.code}
                          onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value })}
                          placeholder="USD, EUR, GBP"
                          className="mt-1"
                          maxLength={3}
                        />
                      </div>
                      <div>
                        <Label>Sembol *</Label>
                        <Input
                          value={newCurrency.symbol}
                          onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                          placeholder="$, €, £"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddCurrencyModal(false)}>
                        İptal
                      </Button>
                      <Button onClick={handleAddCurrency} className="bg-teal-600 hover:bg-teal-700">
                        Ekle
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Ana Para Birimi */}
              <Card className="p-4 bg-teal-50 border-teal-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-teal-900">Ana Para Birimi</h4>
                    <p className="text-sm text-teal-700">Tüm raporlar ve hesaplamalar bu para birimi üzerinden yapılır</p>
                  </div>
                  <select
                    value={baseCurrency}
                    onChange={(e) => handleUpdateBaseCurrency(e.target.value)}
                    className="px-4 py-2 border border-teal-300 rounded-md bg-white"
                  >
                    {MAJOR_CURRENCIES.map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.code} ({curr.symbol}) - {curr.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Card>

              {/* Para Birimler Listesi */}
              {currenciesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Yükleniyor...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currencies.map((currency) => (
                    <Card key={currency.code} className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-2xl font-bold">{currency.symbol}</div>
                          <div className="text-sm text-gray-600">{currency.code}</div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCurrency(currency.code)}
                          className="text-red-600 hover:text-red-700"
                          disabled={currency.code === baseCurrency}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Ülkeler Tab */}
          <TabsContent value="countries">
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">Ülkeler</h3>
                  <p className="text-sm text-gray-500">Ülke listesini yönetin</p>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    placeholder="Ülke ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button onClick={() => setShowAddCountryModal(true)} className="bg-teal-600 hover:bg-teal-700">
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Ülke Ekle
                  </Button>
                </div>
              </div>

              {countriesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Yükleniyor...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredCountries.map((country) => (
                    <Card key={country.id} className="p-3 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <GlobeIcon className="h-4 w-4 text-teal-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium block truncate">{getDisplayCountryName(country.name)}</span>
                            <span className="text-xs text-gray-500">{country.code}</span>
                          </div>
                        </div>
                        {!country.is_system && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleEditCountry(country)}
                              className="p-1 text-teal-600 hover:bg-teal-50 rounded transition-colors"
                              title="Düzenle"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteCountry(country.id, country.is_system)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Sil"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {!countriesLoading && filteredCountries.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <GlobeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Arama sonucu bulunamadı</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Ülke Ekle Modal */}
        <Dialog open={showAddCountryModal} onOpenChange={setShowAddCountryModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Ülke Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="country-name">Ülke Adı *</Label>
                <Input
                  id="country-name"
                  placeholder="Örn: Türkiye"
                  value={countryFormData.name}
                  onChange={(e) => setCountryFormData({...countryFormData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country-code">Ülke Kodu *</Label>
                <Input
                  id="country-code"
                  placeholder="Örn: TR"
                  maxLength={3}
                  value={countryFormData.code}
                  onChange={(e) => setCountryFormData({...countryFormData, code: e.target.value.toUpperCase()})}
                />
                <p className="text-xs text-gray-500">2-3 harfli ülke kodu</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCountryModal(false)}>
                İptal
              </Button>
              <Button onClick={handleAddCountry} className="bg-teal-600 hover:bg-teal-700">
                Ekle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ülke Düzenle Modal */}
        <Dialog open={showEditCountryModal} onOpenChange={setShowEditCountryModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ülke Düzenle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-country-name">Ülke Adı *</Label>
                <Input
                  id="edit-country-name"
                  placeholder="Örn: Türkiye"
                  value={countryFormData.name}
                  onChange={(e) => setCountryFormData({...countryFormData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-country-code">Ülke Kodu *</Label>
                <Input
                  id="edit-country-code"
                  placeholder="Örn: TR"
                  maxLength={3}
                  value={countryFormData.code}
                  onChange={(e) => setCountryFormData({...countryFormData, code: e.target.value.toUpperCase()})}
                />
                <p className="text-xs text-gray-500">2-3 harfli ülke kodu</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditCountryModal(false)}>
                İptal
              </Button>
              <Button onClick={handleUpdateCountry} className="bg-teal-600 hover:bg-teal-700">
                Güncelle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
};

export default Library;
